// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { documentsApi } from '@shared/data-access/api/engine/documents-api';
import type {
  Document,
  DocumentFile,
} from '@shared/data-access/types/engine-types';
import { DocumentScope } from '@shared/data-access/types/engine-types';
import { useDrawer } from '@shared/ui-kit/hooks/use-drawer';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDeleteDocumentConfirm } from '../../hooks/use-delete-document-confirm';
import { useDocumentCatalog } from '../../hooks/use-document-catalog';
import { downloadAllDocumentFiles } from '../../utils/download-document-files';

export interface UseDocumentsDetailDrawerProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly document: Document | null;
  readonly onDeleted?: () => void;
}

const scopeChipColor: Record<DocumentScope, 'default' | 'warning' | 'info'> = {
  [DocumentScope.Personal]: 'default',
  [DocumentScope.Company]: 'warning',
  [DocumentScope.SilverAlpha]: 'info',
};

export const useDocumentsDetailDrawer = ({
  open,
  onClose,
  document,
  onDeleted,
}: UseDocumentsDetailDrawerProps) => {
  const { t, i18n } = useTranslation();
  const { categoryLabels, subcategoryLabels } = useDocumentCatalog();
  const [DrawerRenderer, drawerControls] = useDrawer({ open, onClose });
  const { confirmDeleteDocument } = useDeleteDocumentConfirm({ onDeleted });

  const [fetchDocumentDetail, fetchDocumentDetailState] =
    documentsApi.useFetchDocumentDetailMutation();

  const [detailFiles, setDetailFiles] = useState<DocumentFile[]>([]);
  const [filesError, setFilesError] = useState(false);
  const [isDownloadingAll, setIsDownloadingAll] = useState(false);

  useEffect(() => {
    if (!open || !document?.id) {
      setDetailFiles([]);
      setFilesError(false);
      return;
    }

    let cancelled = false;

    const loadFiles = async () => {
      setFilesError(false);

      try {
        const detail = await fetchDocumentDetail({
          document_id: document.id,
        }).unwrap();

        if (!cancelled) {
          setDetailFiles(detail.files);
        }
      } catch {
        if (!cancelled) {
          setDetailFiles([]);
          setFilesError(true);
        }
      }
    };

    void loadFiles();

    return () => {
      cancelled = true;
    };
  }, [document?.id, fetchDocumentDetail, open]);

  const scopeLabel = document
    ? t(`documents.scope.${document.scope}`, { ns: 'components' })
    : '';

  const onDownloadAllFiles = useCallback(() => {
    if (detailFiles.length === 0) {
      return;
    }

    setIsDownloadingAll(true);

    try {
      downloadAllDocumentFiles(detailFiles);
    } finally {
      setIsDownloadingAll(false);
    }
  }, [detailFiles]);

  const onRetryLoadFiles = useCallback(async () => {
    if (!document?.id) {
      return;
    }

    setFilesError(false);

    try {
      const detail = await fetchDocumentDetail({
        document_id: document.id,
      }).unwrap();
      setDetailFiles(detail.files);
    } catch {
      setFilesError(true);
      setDetailFiles([]);
    }
  }, [document?.id, fetchDocumentDetail]);

  const onDelete = useCallback(() => {
    if (!document) {
      return;
    }

    confirmDeleteDocument(document, { onClose });
  }, [confirmDeleteDocument, document, onClose]);

  const isFilesLoading = fetchDocumentDetailState.isLoading;
  const isDownloadDisabled =
    isFilesLoading || isDownloadingAll || detailFiles.length === 0 || filesError;

  return {
    t,
    i18n,
    DrawerRenderer,
    ...drawerControls,
    document,
    detailFiles,
    isFilesLoading,
    filesError,
    scopeLabel,
    scopeChipColor,
    onDownloadAllFiles,
    onRetryLoadFiles,
    onDelete,
    isDownloadDisabled,
    isDownloadingAll,
    categoryLabels,
    subcategoryLabels,
  };
};
